import { setContext } from '@apollo/client/link/context';
import { ApolloLink } from '@apollo/client/link/core';
import { IS_DEVELOPMENT } from '@/lib/constants';
import { GATEWAY_URL } from '@/lib/env';
import { logger as globalLogger } from '@/logger';
import { refreshPromise } from '@/stores/auth';

const HEADER_NAME = 'X-Csrf-Token';
const logger = globalLogger.getLogger('apollo/csrf');

let csrfToken: string | undefined = undefined;
let refreshCsrfPromise: Promise<void> | undefined = undefined;

const DownLink = setContext(async (_operation, context: ApolloContext) => {
  if (context.skipCsrf) {
    return context;
  }

  await refreshPromise;
  await refreshCsrfPromise;

  if (csrfToken) {
    if (IS_DEVELOPMENT) {
      logger.trace(`Set '${HEADER_NAME}' to '${csrfToken}'`);
    }

    return {
      ...context,
      headers: {
        ...context.headers,
        [HEADER_NAME]: csrfToken,
      },
    };
  }

  return context;
});

const UpLink = new ApolloLink((operation, forward) =>
  forward(operation).map((data) => {
    const response = (operation.getContext() as ApolloContext).response;

    if (response) {
      const value = retrieveCsrfFromResponse(response);

      if (value) {
        csrfToken = value;

        if (IS_DEVELOPMENT) {
          logger.trace(`Received '${HEADER_NAME}' as '${csrfToken}'`);
        }
      }
    }

    return data;
  }),
);

export const CsrfLinks = [DownLink, UpLink];

export async function refreshCsrf() {
  logger.trace('Refreshing');

  refreshCsrfPromise ??= (async () => {
    logger.trace('Fetching');

    const response = await fetch(`${GATEWAY_URL}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
    });

    csrfToken = retrieveCsrfFromResponse(response);

    if (IS_DEVELOPMENT) {
      logger.trace(`Received '${HEADER_NAME}' as '${csrfToken}'`);
    }
  })();

  try {
    await refreshCsrfPromise;
    logger.trace('Refreshed');
  } catch (error) {
    logger.error('Refresh', { error });
  } finally {
    refreshCsrfPromise = undefined;
  }
}

function retrieveCsrfFromResponse(response: Response) {
  return response.headers.get(HEADER_NAME) || undefined;
}

export function getCsrfHeaders(): HeadersInit {
  return { [HEADER_NAME]: csrfToken || '' };
}

export function clearCsrf() {
  csrfToken = undefined;
  logger.trace('Cleared');
}
